/* eslint-disable consistent-return */
/* eslint-disable max-lines */

import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectCanShowBulkNotification, webSocketActions } from 'store/slices/webSocketSlice';

const SnackbarActions = ({ snackbarId, onRefresh, closeSnackbar }) => (
  <>
    <button
      type='button'
      onClick={() => {
        if (onRefresh) {
          onRefresh();
        }
        closeSnackbar(snackbarId);
      }}
      style={{
        background: 'none',
        border: 'none',
        color: 'inherit',
        textDecoration: 'underline',
        cursor: 'pointer',
        marginLeft: '8px',
      }}
    >
      Refresh
    </button>
    <IconButton
      size='small'
      aria-label='close'
      color='inherit'
      onClick={() => closeSnackbar(snackbarId)}
      sx={{ ml: 1 }}
    >
      <CloseIcon fontSize='small' />
    </IconButton>
  </>
);

SnackbarActions.propTypes = {
  snackbarId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onRefresh: PropTypes.func,
  closeSnackbar: PropTypes.func.isRequired,
};

SnackbarActions.defaultProps = {
  onRefresh: null,
};

const useBulkQueueEvents = (existingSocket, onRefresh) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [messages, setMessages] = useState([]);
  const [isBulkRoomJoined, setIsBulkRoomJoined] = useState(false);
  const bulkRoomRef = useRef(null);
  const eventListenersRef = useRef({});

  const currentUserId = useSelector((state) => state.auth?.userId);
  const canShowNotification = useSelector(selectCanShowBulkNotification);

  useEffect(() => {
    if (!existingSocket) return;

    // Clean up previous listeners
    Object.values(eventListenersRef.current).forEach((callback) => {
      if (callback) {
        existingSocket.off('bulk_room_joined', callback);
        existingSocket.off('user_joined_bulk_room', callback);
        existingSocket.off('user_left_bulk_room', callback);
        existingSocket.off('bulk_items_printed', callback);
        existingSocket.off('bulk_items_filled', callback);
        existingSocket.off('labels_regenerated', callback);
        existingSocket.off('line_items_locked', callback);
        existingSocket.off('line_items_unlocked', callback);
        existingSocket.off('line_items_expired', callback);
        existingSocket.off('app_locked_line_items', callback);
      }
    });
    eventListenersRef.current = {};

    const handleBulkRoomJoined = (data) => {
      setMessages((prev) => [
        ...prev,
        { event: 'bulk_room_joined', data, timestamp: new Date().toISOString() },
      ]);

      if (data.currentUsers) {
        dispatch(
          webSocketActions.updateBulkRoomUsers({
            currentUsers: data.currentUsers,
            roomName: data.roomName,
            message: data.message,
          })
        );
      }
    };

    const handleUserJoinedBulkRoom = (data) => {
      setMessages((prev) => [
        ...prev,
        { event: 'user_joined_bulk_room', data, timestamp: new Date().toISOString() },
      ]);

      if (data.newUser && data.allUsersInRoom) {
        dispatch(
          webSocketActions.addUserToBulkRoom({
            newUser: data.newUser,
          })
        );
        dispatch(
          webSocketActions.updateBulkRoomUsers({
            currentUsers: data.allUsersInRoom,
            roomName: 'bulk_room',
            message: data.message,
          })
        );
      }
    };

    const handleUserLeftBulkRoom = (data) => {
      setMessages((prev) => [
        ...prev,
        { event: 'user_left_bulk_room', data, timestamp: new Date().toISOString() },
      ]);

      if (data.leftUser && data.remainingUsers) {
        dispatch(
          webSocketActions.removeUserFromBulkRoom({
            leftUser: data.leftUser,
          })
        );
        dispatch(
          webSocketActions.updateBulkRoomUsers({
            currentUsers: data.remainingUsers,
            roomName: 'bulk_room',
            message: data.message,
          })
        );
      }
    };

    const handleBulkItemsPrinted = (data) => {
      setMessages((prev) => [
        ...prev,
        { event: 'bulk_items_printed', data, timestamp: new Date().toISOString() },
      ]);

      if (data.actionUser?.pomUserId && data.actionUser.pomUserId !== currentUserId) {
        if (canShowNotification) {
          enqueueSnackbar(
            'Bulk items have been updated. Please refresh the list to see the updated list.',
            {
              preventDuplicate: true,
              variant: 'info',
              autoHideDuration: 30000,
              action: (snackbarId) => (
                <SnackbarActions
                  snackbarId={snackbarId}
                  onRefresh={onRefresh}
                  closeSnackbar={closeSnackbar}
                />
              ),
            }
          );

          dispatch(webSocketActions.setBulkNotificationTime(new Date().toISOString()));
        }
      }
    };

    const handleBulkItemsFilled = (data) => {
      setMessages((prev) => [
        ...prev,
        { event: 'bulk_items_filled', data, timestamp: new Date().toISOString() },
      ]);

      if (data.actionUser?.pomUserId && data.actionUser.pomUserId !== currentUserId) {
        if (canShowNotification) {
          enqueueSnackbar(
            'Bulk items have been updated. Please refresh the list to see the updated list.',
            {
              preventDuplicate: true,
              variant: 'info',
              autoHideDuration: 30000,
              action: (snackbarId) => (
                <SnackbarActions
                  snackbarId={snackbarId}
                  onRefresh={onRefresh}
                  closeSnackbar={closeSnackbar}
                />
              ),
            }
          );

          dispatch(webSocketActions.setBulkNotificationTime(new Date().toISOString()));
        }
      }
    };

    const handleLineItemsLocked = (data) => {
      const timestamp = new Date().toISOString();
      const message = { event: 'line_items_locked', data, timestamp };

      setMessages((prev) => [...prev, message]);
      dispatch(webSocketActions.addWebSocketMessage(message));
    };

    const handleLineItemsUnlocked = (data) => {
      const timestamp = new Date().toISOString();
      const message = { event: 'line_items_unlocked', data, timestamp };

      setMessages((prev) => [...prev, message]);
      dispatch(webSocketActions.addWebSocketMessage(message));
    };

    const handleLineItemsExpired = (data) => {
      const timestamp = new Date().toISOString();
      const message = { event: 'line_items_expired', data, timestamp };

      setMessages((prev) => [...prev, message]);
      dispatch(webSocketActions.addWebSocketMessage(message));
    };

    const handleAppLockedLineItems = (data) => {
      const timestamp = new Date().toISOString();
      const message = { event: 'app_locked_line_items', data, timestamp };

      setMessages((prev) => [...prev, message]);
      dispatch(webSocketActions.addWebSocketMessage(message));
    };

    const handleLabelsRegenerated = (data) => {
      const timestamp = new Date().toISOString();
      const message = { event: 'labels_regenerated', data, timestamp };

      setMessages((prev) => [...prev, message]);
      dispatch(webSocketActions.addWebSocketMessage(message));
    };

    existingSocket.on('bulk_room_joined', handleBulkRoomJoined);
    existingSocket.on('user_joined_bulk_room', handleUserJoinedBulkRoom);
    existingSocket.on('user_left_bulk_room', handleUserLeftBulkRoom);
    existingSocket.on('bulk_items_printed', handleBulkItemsPrinted);
    existingSocket.on('bulk_items_filled', handleBulkItemsFilled);
    existingSocket.on('line_items_locked', handleLineItemsLocked);
    existingSocket.on('line_items_unlocked', handleLineItemsUnlocked);
    existingSocket.on('line_items_expired', handleLineItemsExpired);
    existingSocket.on('app_locked_line_items', handleAppLockedLineItems);
    existingSocket.on('labels_regenerated', handleLabelsRegenerated);

    eventListenersRef.current = {
      bulk_room_joined: handleBulkRoomJoined,
      user_joined_bulk_room: handleUserJoinedBulkRoom,
      user_left_bulk_room: handleUserLeftBulkRoom,
      bulk_items_printed: handleBulkItemsPrinted,
      bulk_items_filled: handleBulkItemsFilled,
      line_items_locked: handleLineItemsLocked,
      line_items_unlocked: handleLineItemsUnlocked,
      line_items_expired: handleLineItemsExpired,
      app_locked_line_items: handleAppLockedLineItems,
      labels_regenerated: handleLabelsRegenerated,
    };

    return () => {
      if (existingSocket) {
        existingSocket.off('bulk_room_joined', handleBulkRoomJoined);
        existingSocket.off('user_joined_bulk_room', handleUserJoinedBulkRoom);
        existingSocket.off('user_left_bulk_room', handleUserLeftBulkRoom);
        existingSocket.off('bulk_items_printed', handleBulkItemsPrinted);
        existingSocket.off('bulk_items_filled', handleBulkItemsFilled);
        existingSocket.off('line_items_locked', handleLineItemsLocked);
        existingSocket.off('line_items_unlocked', handleLineItemsUnlocked);
        existingSocket.off('line_items_expired', handleLineItemsExpired);
        existingSocket.off('app_locked_line_items', handleAppLockedLineItems);
        existingSocket.off('labels_regenerated', handleLabelsRegenerated);
      }
    };
  }, [
    existingSocket,
    dispatch,
    currentUserId,
    enqueueSnackbar,
    closeSnackbar,
    onRefresh,
    canShowNotification,
  ]);

  const joinBulkRoom = useCallback(() => {
    if (existingSocket && existingSocket.connected) {
      existingSocket.emit('join_bulk_room');
      setIsBulkRoomJoined(true);
      bulkRoomRef.current = 'bulk_room';
    }
  }, [existingSocket]);

  const leaveBulkRoom = useCallback(() => {
    if (existingSocket && existingSocket.connected && bulkRoomRef.current) {
      // Emit leave_bulk_room event to notify server
      existingSocket.emit('leave_bulk_room');
      dispatch(webSocketActions.clearBulkRoomUsers());

      setIsBulkRoomJoined(false);
      bulkRoomRef.current = null;
    }
  }, [existingSocket, dispatch]);

  const sendMessageToBulkRoom = useCallback(
    (messageData) => {
      if (existingSocket && existingSocket.connected && bulkRoomRef.current) {
        existingSocket.emit('send_bulk_message', messageData);
      }
    },
    [existingSocket]
  );

  // Single cleanup effect that handles both component unmount and beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (bulkRoomRef.current && existingSocket && existingSocket.connected) {
        existingSocket.emit('leave_bulk_room');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Cleanup on component unmount
      if (bulkRoomRef.current) {
        leaveBulkRoom();
      }
    };
  }, [leaveBulkRoom, existingSocket]);

  const clearMessages = () => {
    setMessages([]);
  };

  const onBulkRoomEvent = (event, callback) => {
    if (existingSocket) {
      const wrappedCallback = (data) => {
        setMessages((prev) => [...prev, { event, data, timestamp: new Date().toISOString() }]);
        callback(data);
      };
      existingSocket.on(event, wrappedCallback);
      return wrappedCallback;
    }
    return null;
  };

  const offBulkRoomEvent = (event, callback) => {
    if (existingSocket && callback) {
      existingSocket.off(event, callback);
    }
  };

  return {
    isConnected: existingSocket?.connected || false,
    isBulkRoomJoined,

    sendMessageToBulkRoom,
    joinBulkRoom,
    leaveBulkRoom,

    onBulkRoomEvent,
    offBulkRoomEvent,

    messages,
    clearMessages,
    currentRoom: bulkRoomRef.current,
  };
};

export default useBulkQueueEvents;
